import React from "react";
import { FormProvider, useForm } from "react-hook-form";

type SortByUI = "match_date" | "updated_at";
type SortOrder = "asc" | "desc";

type ApiSortBy = "scheduled_at" | "updated_at" | "matchday_index";

const SORT_BY: readonly SortByUI[] = ["match_date", "updated_at"] as const;
const SORT_ORDER: readonly SortOrder[] = ["asc", "desc"] as const;

export interface MatchFilterState {
  page?: number | string;
  limit?: number | string;
  sortBy?: string;
  sortOrder?: "asc" | "desc" | string;

  search?: string;
  id?: string;

  competition?: {
    country?: string;
    competition_id?: string;
    competition_name?: string;
    club_id?: string;
    club_name?: string;
  };

  daterange?: {
    date_from?: string;
    date_from_display?: string;
    date_to?: string;
    date_to_display?: string;
  };
}

export const normalizeFilterInput = (filters: MatchFilterState) => {
  const uiSortBy =
    filters.sortBy && SORT_BY.includes(filters.sortBy as SortByUI)
      ? (filters.sortBy as SortByUI)
      : undefined;

  const mapToApi: Record<SortByUI, ApiSortBy> = {
    match_date: "scheduled_at",
    updated_at: "updated_at",
  };
  const sortBy: ApiSortBy | undefined = uiSortBy ? mapToApi[uiSortBy] : undefined;

  const sortOrder =
    filters.sortOrder && SORT_ORDER.includes(filters.sortOrder as SortOrder)
      ? (filters.sortOrder as SortOrder)
      : undefined;

  const page = filters.page ? Number(filters.page) : undefined;
  const pageSize = filters.limit ? Number(filters.limit) : undefined;

  return {
    page,
    pageSize,
    sortBy,
    sortOrder,
    search: filters.search,
    id: filters.id,

    competition_id: filters.competition?.competition_id,
    club_id: filters.competition?.club_id,

    daterange: filters.daterange
      ? {
          date_from: filters.daterange.date_from,
          date_to: filters.daterange.date_to,
        }
      : undefined,

    includeSides: true,
    includeStarters: false,
  };
};

export const buildDefaultFilterState = (): MatchFilterState => ({
  page: 1,
  limit: 50,
  sortBy: "match_date",
  sortOrder: "asc",
  competition: {
    club_id: undefined,
    competition_id: undefined,
  },
  daterange: {
    date_from: undefined,
    date_to: undefined,
  },
  search: undefined,
  id: undefined,
});

const FilterProvider = ({ children }: { children: React.ReactNode }) => {
  const defaultValues = React.useMemo(() => buildDefaultFilterState(), []);
  const form = useForm<MatchFilterState>({
    defaultValues,
  });

  return <FormProvider {...form}>{children}</FormProvider>;
};

export default FilterProvider;
